import { useEffect, useId, useRef, useState } from 'react'
import { FileDown } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { StatusNotice } from './WorkflowLayout'

export const privacyWarning =
  'This file contains personal information, including names and operational comments, and is not encrypted. Store and share it securely.'
export function JsonFile({
  label,
  onLoad,
  disabled = false,
  onStart,
  onReading,
  maxMiB = 32,
  dropzone = false,
}: {
  label: string
  maxMiB?: number
  onLoad: (value: unknown, filename?: string) => void
  disabled?: boolean
  onStart?: () => void
  onReading?: (reading: boolean) => void
  dropzone?: boolean
}) {
  const [error, setError] = useState('')
  const [dragging, setDragging] = useState(false)
  const version = useRef(0),
    input = useRef<HTMLInputElement>(null)
  useEffect(
    () => () => {
      version.current++
    },
    []
  )
  const id = useId()
  async function read(file?: File) {
    if (!file || disabled || input.current?.matches(':disabled')) return
    const current = ++version.current
    setError('')
    onStart?.()
    onReading?.(true)
    try {
      if (file.size > maxMiB * 1024 * 1024)
        throw new RangeError(`File exceeds the ${maxMiB} MiB size limit.`)
      const value: unknown = JSON.parse(await file.text())
      if (current === version.current) onLoad(value, file.name)
    } catch (error) {
      if (current === version.current)
        setError(
          error instanceof RangeError
            ? error.message
            : error instanceof SyntaxError
              ? 'Malformed JSON. Choose the original exported file or export a new copy.'
              : 'Incompatible package or recovery file. Choose a supported Honey Show file.'
        )
    } finally {
      if (current === version.current) onReading?.(false)
    }
  }
  return (
    <div className="space-y-3">
      <div
        className={
          dropzone
            ? `space-y-4 rounded-lg border border-dashed p-6 text-center ${dragging ? 'border-primary bg-accent' : 'border-muted-foreground/50 bg-muted/30'}`
            : 'space-y-2'
        }
        onDragOver={
          dropzone
            ? event => {
                event.preventDefault()
                if (!disabled) setDragging(true)
              }
            : undefined
        }
        onDragLeave={dropzone ? () => setDragging(false) : undefined}
        onDrop={
          dropzone
            ? event => {
                event.preventDefault()
                setDragging(false)
                void read(event.dataTransfer.files[0])
              }
            : undefined
        }
      >
        {dropzone && (
          <FileDown className="mx-auto size-6 text-muted-foreground" aria-hidden="true" />
        )}
        <label htmlFor={id} className="block text-sm font-medium">
          {label}
        </label>
        {dropzone && (
          <p className="text-sm text-muted-foreground">
            Drop a JSON file here, or browse your computer. Up to {maxMiB} MiB.
          </p>
        )}
        <Input
          ref={input}
          id={id}
          type="file"
          aria-label={label}
          accept=".json,application/json"
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          onChange={event => {
            void read(event.target.files?.[0])
            event.target.value = ''
          }}
        />
      </div>
      {error && (
        <div id={`${id}-error`}>
          <StatusNotice tone="error" title="This file can’t be used">
            {error}
          </StatusNotice>
        </div>
      )}
    </div>
  )
}
export function Check({
  children,
  checked,
  onChange,
  disabled,
}: {
  children: React.ReactNode
  checked: boolean
  onChange: (value: boolean) => void
  disabled?: boolean
}) {
  return (
    <label className="flex items-start gap-3 text-sm">
      <input
        className="mt-1 accent-primary"
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={e => onChange(e.target.checked)}
      />
      {children}
    </label>
  )
}
